import { Link } from 'react-router';
import {
  EyeIcon,
  LogOutIcon,
  ScrollTextIcon,
  SettingsIcon,
  TestTubeDiagonalIcon,
  UsersIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { cn } from '@/lib/utils.js';

const ITEMS = [
  { to: '/resolve', label: 'Test a URL', Icon: TestTubeDiagonalIcon },
  { to: '/users', label: 'Users', Icon: UsersIcon },
  { to: '/audit', label: 'Audit log', Icon: ScrollTextIcon },
  { to: '/settings', label: 'Settings', Icon: SettingsIcon },
];

/**
 * The initial shown on the avatar.
 *
 * Deliberately tolerant: a session can carry an empty or whitespace-only email
 * (a fixture, a half-migrated row), and a header must not throw over it.
 * `Array.from` rather than `email[0]` so a leading astral character — an emoji
 * in a display alias — yields one whole glyph instead of half a surrogate pair.
 */
export function accountInitial(email: string): string {
  const first = Array.from(email.trim())[0];
  return first === undefined ? '?' : first.toUpperCase();
}

export type AccountMenuProps = {
  email: string;
  role: 'admin' | 'viewer';
  onSignOut: () => void;
};

/**
 * The account avatar and its menu.
 *
 * Radix's dropdown carries the roles, focus management, arrow-key traversal,
 * Escape and click-outside; the items are real `Link`s inside it so the
 * destinations are still openable in a new tab and legible to a crawler of the
 * accessibility tree.
 *
 * The role badge lives here rather than in the header: a viewer needs to know
 * *why* the write controls are missing, and this is the one place that names
 * who they are signed in as.
 */
export function AccountMenu({ email, role, onSignOut }: AccountMenuProps) {
  return (
    <DropdownMenu
      /*
       * Not modal, deliberately.
       *
       * A modal Radix menu locks the document's scroll while it is open, and
       * removing the scrollbar re-centres every centred container on the page
       * — the whole layout jumps sideways by half the scrollbar's width as
       * the menu opens, and back as it closes. A dropdown is not a modal: the
       * page behind it stays legible and scrollable, and nothing moves.
       *
       * `scrollbar-gutter: stable` on the document does not save this case;
       * the lock is applied to `body`, so the reserved gutter on the scrolling
       * element is not what disappears.
       */
      modal={false}
    >
      <DropdownMenuTrigger
        type="button"
        aria-label={`Account menu for ${email}`}
        className={cn(
          'grid size-8 shrink-0 place-items-center rounded-full',
          'bg-accent-quiet text-accent',
          'text-[length:var(--text-small)] font-semibold',
          'transition-colors duration-(--duration-fast) hover:bg-surface-hover hover:text-ink',
        )}
      >
        <span aria-hidden="true">{accountInitial(email)}</span>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="min-w-[14rem]">
        {/* Plain text, not a menu item: it is who you are, not something to do. */}
        <p className="px-(--space-2) py-(--space-2) text-[length:var(--text-small)] text-ink-muted">
          <span className="block break-all text-ink">{email}</span>
          {role === 'viewer' && (
            <span className="mt-(--space-1) inline-flex items-center gap-(--space-1) text-[length:var(--text-micro)] uppercase tracking-wide text-ink-subtle">
              <EyeIcon className="size-3" aria-hidden="true" />
              Read-only
            </span>
          )}
        </p>

        <DropdownMenuSeparator />

        {ITEMS.map(({ to, label, Icon }) => (
          <DropdownMenuItem key={to} asChild>
            <Link to={to}>
              <Icon aria-hidden="true" />
              {label}
            </Link>
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={onSignOut}>
          <LogOutIcon aria-hidden="true" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
